import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const EvaluationTrackerPage = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [searchEvalId, setSearchEvalId] = useState('');
  const [searchOrderId, setSearchOrderId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dueDateFilter, setDueDateFilter] = useState('');
  const [wordCountFilter, setWordCountFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [plagiarismFilter, setPlagiarismFilter] = useState('');
  const [contentRelevanceFilter, setContentRelevanceFilter] = useState('');
  const [finalScoreFilter, setFinalScoreFilter] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchEvaluations = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = '/employee-login';
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/evaluation-tracker`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setEvaluations(response.data);
        setFilteredData(response.data);
      } catch (err) {
        console.error('Error fetching evaluations', err);
      }
    };

    fetchEvaluations();
  }, [apiBaseURL]);

  useEffect(() => {
    let filtered = [...evaluations];

    if (searchEvalId) {
      filtered = filtered.filter(ev => ev.evaluation_id.toString().includes(searchEvalId));
    }

    if (searchOrderId) {
      filtered = filtered.filter(ev => ev.order_id?.toString().includes(searchOrderId));
    }

    if (statusFilter) {
      filtered = filtered.filter(ev => ev.evaluation_status === statusFilter);
    }

    const boolFilters = [
      { field: 'cwe_a1_01_valid_due_date', value: dueDateFilter },
      { field: 'cwe_a1_02_valid_word_count', value: wordCountFilter },
      { field: 'cwe_a1_03_valid_language', value: languageFilter },
      { field: 'cwe_a1_04_valid_plagiarism', value: plagiarismFilter },
      { field: 'cwe_a1_05_valid_content_relevance', value: contentRelevanceFilter }
    ];

    boolFilters.forEach(({ field, value }) => {
      if (value !== '') {
        filtered = filtered.filter(ev => ev[field] === (value === 'true'));
      }
    });

    if (finalScoreFilter) {
      filtered = filtered.filter(ev =>
        finalScoreFilter === 'lt65' ? ev.cwe_final_score < 65 :
        finalScoreFilter === 'gte65' ? ev.cwe_final_score >= 65 : true
      );
    }

    setFilteredData(filtered);
  }, [
    searchEvalId, searchOrderId, statusFilter,
    dueDateFilter, wordCountFilter, languageFilter, plagiarismFilter, contentRelevanceFilter,
    finalScoreFilter, evaluations
  ]);

  const handleDownloadExcel = () => {
    const now = new Date();
    const formattedTime = now.toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    const formattedHeaderTime = now.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const fileName = `Evaluation_Tracker_${formattedTime}.xlsx`;

    const sheetHeader = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Evaluation Tracker'],
      [`Downloaded on: ${formattedHeaderTime}`],
      [],
    ];

    const tableHeaders = Object.keys(filteredData[0] || {});
    const tableData = filteredData.map(row =>
      tableHeaders.map(key => {
        const val = row[key];
        return val === true ? '✅' : val === false ? '❌' : val ?? '—';
      })
    );

    const worksheetData = [...sheetHeader, tableHeaders, ...tableData];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Evaluation Tracker');

    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="flex-1 p-6 bg-gray-50 ml-64">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">Evaluation Tracker</h2>
        <button
          onClick={handleDownloadExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Download Excel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <input value={searchEvalId} onChange={e => setSearchEvalId(e.target.value)} placeholder="Evaluation ID" className="border p-2 rounded w-40" />
        <input value={searchOrderId} onChange={e => setSearchOrderId(e.target.value)} placeholder="Order ID" className="border p-2 rounded w-40" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border p-2 rounded w-40">
          <option value="">All Status</option>
          {Array.from(new Set(evaluations.map(ev => ev.evaluation_status))).map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <select value={finalScoreFilter} onChange={e => setFinalScoreFilter(e.target.value)} className="border p-2 rounded w-40">
          <option value="">All Scores</option>
          <option value="lt65">Less than 65</option>
          <option value="gte65">65 and above</option>
        </select>

        {/* Boolean Filters */}
        {[
          { label: 'Due Date', value: dueDateFilter, setter: setDueDateFilter },
          { label: 'Word Count', value: wordCountFilter, setter: setWordCountFilter },
          { label: 'Language', value: languageFilter, setter: setLanguageFilter },
          { label: 'Plagiarism', value: plagiarismFilter, setter: setPlagiarismFilter },
          { label: 'Content Relevance', value: contentRelevanceFilter, setter: setContentRelevanceFilter },
        ].map(({ label, value, setter }) => (
          <select key={label} value={value} onChange={e => setter(e.target.value)} className="border p-2 rounded w-44">
            <option value="">{label}</option>
            <option value="true">✅ True</option>
            <option value="false">❌ False</option>
          </select>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto bg-white shadow rounded-lg text-sm">
          <thead>
            <tr className="bg-gray-200">
              {Object.keys(evaluations[0] || {}).map((key, idx) => (
                <th key={idx} className="px-3 py-2 text-left">{key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="100%" className="text-center p-3">No records found.</td>
              </tr>
            ) : (
              filteredData.map((row, idx) => (
                <tr key={idx} className="border-b">
                  {Object.values(row).map((val, colIdx) => (
                    <td key={colIdx} className="px-3 py-2 whitespace-nowrap">
                      {val === true ? '✅' : val === false ? '❌' : val ?? '—'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EvaluationTrackerPage;
